import React, { useState } from 'react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import './signup.css';

const Signup = () => {
  const { signup } = useAuth();
  const navigate = useNavigate();

  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirmPassword, setSignupConfirmPassword] = useState('');
  const [message, setMessage] = useState({ text: '', isSuccess: false });
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showSignupConfirmPassword, setShowSignupConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isValidPassword = (password) => password.length >= 8;

  const handleSignup = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage({ text: '', isSuccess: false });

    const trimmedName = signupName.trim();
    const trimmedEmail = signupEmail.trim();
    const trimmedPassword = signupPassword.trim();
    const trimmedConfirmPassword = signupConfirmPassword.trim();

    if (!trimmedName || !trimmedEmail || !trimmedPassword || !trimmedConfirmPassword) {
      setMessage({ text: 'Please fill in all fields.', isSuccess: false });
      setIsSubmitting(false);
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setMessage({ text: 'Please enter a valid email address.', isSuccess: false });
      setIsSubmitting(false);
      return;
    }

    if (!isValidPassword(trimmedPassword)) {
      setMessage({ text: 'Password must be at least 8 characters long.', isSuccess: false });
      setIsSubmitting(false);
      return;
    }

    if (trimmedPassword !== trimmedConfirmPassword) {
      setMessage({ text: 'Passwords do not match.', isSuccess: false });
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await signup(trimmedName, trimmedEmail, trimmedPassword);

      if (response.success) {
        setMessage({ 
          text: 'Account created successfully! Redirecting to login...', 
          isSuccess: true 
        });
        
        // Clear form fields
        setSignupName('');
        setSignupEmail('');
        setSignupPassword('');
        setSignupConfirmPassword('');

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setMessage({ text: response.message || 'Signup failed. Please try again.', isSuccess: false });
      }
    } catch (error) {
      setMessage({ text: 'An unexpected error occurred. Please try again.', isSuccess: false });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="signup">
      <h3>Create an Account</h3>
      <form onSubmit={handleSignup}>
        <input
          type="text"
          placeholder="Company Name"
          value={signupName}
          onChange={(e) => setSignupName(e.target.value)}
        />
        <input
          type="email"
          placeholder="Company Email"
          value={signupEmail}
          onChange={(e) => setSignupEmail(e.target.value)}
        />
        <div className="password-input">
          <input
            type={showSignupPassword ? 'text' : 'password'}
            placeholder="Password"
            value={signupPassword}
            onChange={(e) => setSignupPassword(e.target.value)}
          />
          <span
            className="password-toggle-icon"
            onClick={() => setShowSignupPassword(!showSignupPassword)}
          >
            {showSignupPassword ? <FaEyeSlash /> : <FaEye />}
          </span>
        </div>
        <div className="password-input">
          <input
            type={showSignupConfirmPassword ? 'text' : 'password'}
            placeholder="Confirm Password"
            value={signupConfirmPassword}
            onChange={(e) => setSignupConfirmPassword(e.target.value)}
            onPaste={(e) => {
              e.preventDefault();
              setMessage({ text: 'Pasting is not allowed in confirm password field.', isSuccess: false });
            }}
          />
          <span
            className="password-toggle-icon"
            onClick={() => setShowSignupConfirmPassword(!showSignupConfirmPassword)}
          >
            {showSignupConfirmPassword ? <FaEyeSlash /> : <FaEye />}
          </span>
        </div>
        <button 
          type="submit" 
          className="signup-submit-btn"
          disabled={isSubmitting}
        >
          <span className="btn-text">
            {isSubmitting ? 'Creating Account...' : 'Sign Up'}
          </span>
        </button>
        
        {message.text && (
          <p className={`message ${message.isSuccess ? 'message-success' : 'message-error'}`}>
            {message.isSuccess ? (
              <>
                <span style={{ display: 'block', marginBottom: '0.5rem' }}>
                  ✓ {message.text}
                </span>
                <span style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                  Please log in to access your account.
                </span>
              </>
            ) : (
              message.text
            )}
          </p>
        )}
      </form>
      <small>
        Already have an account?{' '}
        <span onClick={() => navigate('/login')} className="toggle-link">
          Sign In
        </span>
      </small>
    </div>
  );
};

export default Signup;